import React, { useState, useEffect } from 'react';
import axios from 'axios';

const RoomBlocking = () => {
    const [buildings, setBuildings] = useState([]);
    const [blocks, setBlocks] = useState([]);

    // Form State
    const [selectedBuilding, setSelectedBuilding] = useState('');
    const [selectedRoomType, setSelectedRoomType] = useState(''); // Optional but good for filtering
    const [selectedCategory, setSelectedCategory] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [reason, setReason] = useState('');
    const [loading, setLoading] = useState(false);

    // Derived Options
    const [roomTypeOptions, setRoomTypeOptions] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);

    useEffect(() => {
        fetchMetadata();
        fetchBlocks();
    }, []);

    const fetchMetadata = async () => {
        try {
            // Reusing availability API to get structure of Buildings -> RoomTypes -> Categories
            // We use a date far in future or today just to get structure
            const today = new Date().toISOString().split('T')[0];
            const res = await axios.get(`/api/public/booking/availability?date=${today}&duration=1`);
            if (res.data.status === 'success') {
                setBuildings(res.data.data);
            }
        } catch (err) {
            console.error("Error fetching metadata", err);
        }
    };

    const fetchBlocks = async () => {
        try {
            const res = await axios.get('/api/room_blocks');
            if (res.data.success) {
                setBlocks(res.data.data);
            }
        } catch (err) {
            console.error("Error fetching blocks", err);
        }
    };

    // Handlers
    const handleBuildingChange = (e) => {
        const bId = e.target.value;
        setSelectedBuilding(bId);
        setSelectedRoomType('');
        setSelectedCategory('');

        const b = buildings.find(x => x.building_id === bId);
        setRoomTypeOptions(b ? b.room_types : []);
    };

    const handleRoomTypeChange = (e) => {
        const rtId = e.target.value;
        setSelectedRoomType(rtId);
        setSelectedCategory('');

        const rt = roomTypeOptions.find(x => x.room_type_id === rtId);
        setCategoryOptions(rt ? rt.categories : []);
    };

    const handleBlock = async (e) => {
        e.preventDefault();
        if (!selectedBuilding || !selectedCategory || !fromDate || !toDate) {
            alert("Please fill all fields");
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('uid'); // Assuming Admin is logged in and token is here? Or user ID
            // Ideally we need a valid User ID for 'created_by'. 
            // If we don't have one, this might fail if schema requires it (it does).
            // We need to decode token or just send a dummy if backend doesn't validate strictly via middleware here (we didn't add auth middleware to route).
            // But we need to SEND it in body because controller uses `req.body.created_by`.

            // For now, let's try to get it from a stored 'user' object or prompt user?
            // Assuming 'uid' token implies a logged in user.
            // Just sending a placeholder ID if not available? No, must constitute valid ObjectId.
            // Let's assume the user viewing this page is an Admin and has an ID.

            const payload = {
                building_id: selectedBuilding,
                // room_type_id: selectedRoomType, // Controller determines by Building + Category
                category: selectedCategory, // This is Name? availability returns Name. Controller expects Name? 
                // Wait, Controller query: { building_id, category }. 
                // Schema rooms.js: category is String (Enum 'AC', 'NON-AC').
                // So yes, name is correct.
                from_date: fromDate,
                to_date: toDate,
                reason: reason,
                created_by: "654321654321654321654321" // DUMMY ObjectId if not found? 
                // Real implementation should decode token.
            };

            // Attempt to use real ID if available in localStorage
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
                try {
                    const u = JSON.parse(storedUser);
                    if (u._id) payload.created_by = u._id;
                } catch (e) { }
            }

            const res = await axios.post('/api/room_blocks/create', payload);
            if (res.data.success) {
                alert("Block Created Successfully");
                fetchBlocks();
                // Reset form?
            }
        } catch (err) {
            alert("Error creating block: " + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Unblock this range?")) return;
        try {
            await axios.delete(`/api/room_blocks/${id}`);
            fetchBlocks();
        } catch (err) {
            alert("Error deleting block");
        }
    };

    return (
        <div className="container py-4">
            <h2>Room Blocking (Category Based)</h2>

            <div className="card p-4 my-4 shadow-sm bg-light">
                <form onSubmit={handleBlock}>
                    <div className="row g-3">
                        <div className="col-md-4">
                            <label className="form-label">Building</label>
                            <select className="form-select" value={selectedBuilding} onChange={handleBuildingChange} required>
                                <option value="">Select Building</option>
                                {buildings.map(b => (
                                    <option key={b.building_id} value={b.building_id}>{b.building_name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">Room Type</label>
                            <select className="form-select" value={selectedRoomType} onChange={handleRoomTypeChange} disabled={!selectedBuilding}>
                                <option value="">Select Room Type</option>
                                {roomTypeOptions.map(rt => (
                                    <option key={rt.room_type_id} value={rt.room_type_id}>{rt.room_type_name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">Category</label>
                            <select className="form-select" value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} disabled={!selectedRoomType} required>
                                <option value="">Select Category</option>
                                {categoryOptions.map(cat => (
                                    <option key={cat.category_name} value={cat.category_name}>{cat.category_name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="col-md-3">
                            <label className="form-label">From Date</label>
                            <input type="date" className="form-control" value={fromDate} onChange={e => setFromDate(e.target.value)} required />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">To Date</label>
                            <input type="date" className="form-control" value={toDate} onChange={e => setToDate(e.target.value)} required />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">Reason</label>
                            <input type="text" className="form-control" value={reason} onChange={e => setReason(e.target.value)} placeholder="Maintenance, VIP, etc" />
                        </div>
                        <div className="col-md-2 d-flex align-items-end">
                            <button className="btn btn-danger w-100" type="submit" disabled={loading}>
                                {loading ? 'Blocking...' : 'Block Rooms'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>

            <hr />

            <h4>Active Blocks</h4>
            <div className="table-responsive">
                <table className="table table-bordered table-hover">
                    <thead className="table-dark">
                        <tr>
                            <th>From</th>
                            <th>To</th>
                            <th>Rooms Blocked</th>
                            <th>Reason</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {blocks.map(blk => (
                            <tr key={blk._id}>
                                <td>{new Date(blk.from_date).toLocaleDateString()}</td>
                                <td>{new Date(blk.to_date).toLocaleDateString()}</td>
                                <td>{blk.room_ids?.length || 0} Rooms</td>
                                <td>{blk.reason}</td>
                                <td>
                                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(blk._id)}>Unblock</button>
                                </td>
                            </tr>
                        ))}
                        {blocks.length === 0 && <tr><td colSpan="5" className="text-center">No Active Blocks</td></tr>}
                    </tbody>
                </table>
            </div>

        </div>
    );
};

export default RoomBlocking;
